import { useEffect, useState, useCallback } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import RefreshIcon from '@material-ui/icons/Refresh';
import { githubAuthApiRef } from '../../apis';

interface GitHubPR {
  id: number;
  title: string;
  html_url: string;
  state: string;
  draft: boolean;
  repository_url: string;
  labels: Array<{ name: string; color: string }>;
  updated_at: string;
  user: { login: string };
  pull_request?: { merged_at: string | null };
}

type PRFilter = 'open' | 'closed';
type PRMode = 'authored' | 'assigned';

const useStyles = makeStyles(theme => ({
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  empty: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  listItem: {
    borderRadius: theme.shape.borderRadius,
    paddingRight: theme.spacing(6),
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  repoChip: {
    marginLeft: theme.spacing(1),
    height: 18,
    fontSize: '0.6rem',
  },
  labelChip: {
    marginLeft: theme.spacing(0.5),
    height: 16,
    fontSize: '0.55rem',
  },
  statusChip: {
    marginLeft: theme.spacing(0.5),
    height: 16,
    fontSize: '0.55rem',
    fontWeight: 600,
  },
  secondary: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  openIcon: {
    padding: theme.spacing(0.5),
  },
  toggleGroup: {
    height: 24,
  },
  toggleButton: {
    fontSize: '0.65rem',
    padding: theme.spacing(0, 1),
    textTransform: 'none',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

const ago = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const repoName = (repoUrl: string) => {
  const parts = repoUrl.split('/');
  return parts[parts.length - 1];
};

const prStatusLabel = (pr: GitHubPR) => {
  if (pr.draft) return 'Draft';
  if (pr.pull_request?.merged_at) return 'Merged';
  if (pr.state === 'closed') return 'Closed';
  return 'Open';
};

const prStatusColor = (pr: GitHubPR): string => {
  if (pr.draft) return '#6e7681';
  if (pr.pull_request?.merged_at) return '#8250df';
  if (pr.state === 'closed') return '#cf222e';
  return '#1a7f37';
};

interface MyPullRequestsCardProps {
  mode?: PRMode;
}

export const MyPullRequestsCard = ({ mode = 'authored' }: MyPullRequestsCardProps) => {
  const classes = useStyles();
  const githubAuth = useApi(githubAuthApiRef);
  const [prs, setPrs] = useState<GitHubPR[]>([]);
  const [filter, setFilter] = useState<PRFilter>('open');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const fetchPRs = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const token = await githubAuth.getAccessToken(['repo']);
      const qualifier = mode === 'assigned' ? 'assignee:@me' : 'author:@me';
      const q = encodeURIComponent(
        `is:pr ${qualifier} org:kyndryl-cto is:${filter}`,
      );
      const resp = await fetch(
        `https://api.github.com/search/issues?q=${q}&sort=updated&order=desc&per_page=20`,
        {
          headers: {
            Authorization: `token ${token}`,
            Accept: 'application/vnd.github.v3+json',
          },
        },
      );
      if (!resp.ok) {
        throw new Error(`GitHub API returned ${resp.status}`);
      }
      const data = await resp.json();
      setPrs(data.items ?? []);
    } catch (e: any) {
      setError(e.message ?? 'Could not load pull requests');
    } finally {
      setLoading(false);
    }
  }, [githubAuth, filter, mode]);

  useEffect(() => {
    fetchPRs();
  }, [fetchPRs]);

  const handleFilterChange = (_: any, value: PRFilter | null) => {
    if (value) setFilter(value);
  };

  const title = mode === 'assigned' ? 'Assigned PRs' : 'My Pull Requests';

  const content = () => {
    if (loading) {
      return (
        <div className={classes.loading}>
          <CircularProgress size={24} />
        </div>
      );
    }
    if (error) {
      return <Typography className={classes.empty}>{error}</Typography>;
    }
    if (prs.length === 0) {
      return (
        <Typography className={classes.empty}>
          No {filter} pull requests found.
        </Typography>
      );
    }
    return (
      <List dense disablePadding>
        {prs.slice(0, 8).map(pr => (
          <ListItem key={pr.id} className={classes.listItem}>
            <ListItemText
              primary={pr.title}
              secondary={
                <span className={classes.secondary}>
                  <Chip
                    label={repoName(pr.repository_url)}
                    size="small"
                    variant="outlined"
                    className={classes.repoChip}
                  />
                  <Chip
                    label={prStatusLabel(pr)}
                    size="small"
                    className={classes.statusChip}
                    style={{
                      backgroundColor: prStatusColor(pr),
                      color: '#fff',
                    }}
                  />
                  {pr.labels.slice(0, 2).map(l => (
                    <Chip
                      key={l.name}
                      label={l.name}
                      size="small"
                      className={classes.labelChip}
                      style={{
                        backgroundColor: `#${l.color}`,
                        color:
                          parseInt(l.color, 16) > 0x7fffff ? '#000' : '#fff',
                      }}
                    />
                  ))}
                  <Typography variant="caption" color="textSecondary">
                    {ago(pr.updated_at)}
                  </Typography>
                </span>
              }
            />
            <Tooltip title="Open in GitHub">
              <IconButton
                className={classes.openIcon}
                size="small"
                href={pr.html_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </ListItem>
        ))}
        {prs.length > 8 && (
          <Typography
            variant="caption"
            style={{ padding: 8, display: 'block' }}
          >
            +{prs.length - 8} more
          </Typography>
        )}
      </List>
    );
  };

  return (
    <InfoCard
      title={title}
      subheader={
        prs.length > 0
          ? `${prs.length} ${filter} PR${prs.length === 1 ? '' : 's'}`
          : undefined
      }
      action={
        <div className={classes.header}>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={handleFilterChange}
            size="small"
            className={classes.toggleGroup}
          >
            <ToggleButton value="open" className={classes.toggleButton}>
              Open
            </ToggleButton>
            <ToggleButton value="closed" className={classes.toggleButton}>
              Closed
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchPRs} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </div>
      }
    >
      {content()}
    </InfoCard>
  );
};
